import React, { useEffect, useState } from 'react';
import { CheckCircle, Loader } from 'lucide-react';

interface EmbeddedSignupProps {
  appId: string;
  configId: string;
  solutionId?: string; // Partner solution ID
  onSuccess: (data: { code: string; wabaId: string; phoneNumberId?: string }) => void;
  onError: (error: string) => void;
}

const EmbeddedSignup: React.FC<EmbeddedSignupProps> = ({
  appId,
  configId,
  solutionId,
  onSuccess,
  onError,
}) => {
  const [loading, setLoading] = useState(false);
  const [fbLoaded, setFbLoaded] = useState(false);

  useEffect(() => {
    // Load Facebook SDK
    const loadFacebookSDK = () => {
      if (document.getElementById('facebook-jssdk')) {
        setFbLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';

      script.onload = () => {
        window.fbAsyncInit = function () {
          FB.init({
            appId: appId,
            autoLogAppEvents: true,
            xfbml: true,
            version: 'v18.0',
          });
          setFbLoaded(true);
        };
      };

      document.body.appendChild(script);
    };

    loadFacebookSDK();

    return () => {
      // Cleanup
      const script = document.getElementById('facebook-jssdk');
      if (script) {
        script.remove();
      }
    };
  }, [appId]);

  const launchWhatsAppSignup = () => {
    if (!fbLoaded) {
      onError('Facebook SDK not loaded yet. Please try again.');
      return;
    }

    setLoading(true);

    let tempAccessCode = '';
    let phoneNumberId = '';
    let waBaId = '';

    // Launch Facebook login
    FB.login(
      function (response: any) {
        if (response.authResponse) {
          tempAccessCode = response.authResponse.code;

          if (tempAccessCode) {
            onSuccess({
              code: tempAccessCode,
              wabaId: waBaId,
              phoneNumberId: phoneNumberId,
            });
          } else {
            onError('No access code received from Facebook');
          }
        } else {
          onError('User cancelled login or did not fully authorize.');
        }
        setLoading(false);
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: solutionId ? { solutionID: solutionId } : {},
          sessionInfoVersion: '3',
        },
      }
    );

    // Listen for session info
    const sessionInfoListener = (event: MessageEvent) => {
      if (event.origin !== 'https://www.facebook.com') return;

      try {
        const data = JSON.parse(event.data);

        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          if (data.event === 'FINISH') {
            const { phone_number_id, waba_id } = data.data;
            phoneNumberId = phone_number_id;
            waBaId = waba_id;
          }
        }
      } catch {
        // Non-JSON data, ignore
      }
    };

    window.addEventListener('message', sessionInfoListener);

    // Cleanup listener after 5 minutes
    setTimeout(() => {
      window.removeEventListener('message', sessionInfoListener);
    }, 300000);
  };

  return (
    <div className="text-center">
      {loading ? (
        <div className="flex flex-col items-center justify-center py-8">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Connecting to WhatsApp...
          </h3>
          <p className="text-sm text-gray-600">
            Please complete the setup in the popup window
          </p>
          <p className="text-xs text-red-600 mt-2">
            Do not refresh or redirect this page
          </p>
        </div>
      ) : (
        <div>
          <button
            onClick={launchWhatsAppSignup}
            disabled={!fbLoaded}
            style={{
              backgroundColor: '#1877f2',
              border: 0,
              borderRadius: '4px',
              color: '#fff',
              cursor: fbLoaded ? 'pointer' : 'not-allowed',
              fontFamily: 'Helvetica, Arial, sans-serif',
              opacity: fbLoaded ? 1 : 0.5,
            }}
            className="px-6 py-3 text-lg font-medium inline-flex items-center gap-3"
          >
            {!fbLoaded ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Loading Facebook...
              </>
            ) : (
              <>
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                Connect WhatsApp with Facebook
              </>
            )}
          </button>

          {fbLoaded && (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-green-600">
              <CheckCircle className="w-4 h-4" />
              <span>Facebook SDK Ready</span>
            </div>
          )}
        </div>
      )}

      <div className="mt-6 text-left max-w-md mx-auto bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">What happens next?</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Login with your Facebook account</li>
          <li>• Create or select a Business Account</li>
          <li>• Create or select a WhatsApp Business Account</li>
          <li>• Select or add a phone number</li>
          <li>• Grant necessary permissions</li>
        </ul>
      </div>
    </div>
  );
};

export default EmbeddedSignup;

// TypeScript declaration for FB
declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
  const FB: any;
}
